import { useState, useEffect, useCallback, useRef } from "react";
import {
  Bell, CheckCheck, Loader2, ShoppingBag, XCircle, Store, Truck,
  MessageCircle, Megaphone, CheckCircle2, Pause,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { API } from "../App";

const TYPE_META = {
  order_new: { icon: ShoppingBag, cls: "bg-orange-500/15 text-orange-400" },
  order_cancelled: { icon: XCircle, cls: "bg-rose-500/15 text-rose-400" },
  store_new: { icon: Store, cls: "bg-violet-500/15 text-violet-400" },
  store_activated: { icon: CheckCircle2, cls: "bg-emerald-500/15 text-emerald-400" },
  store_suspended: { icon: Pause, cls: "bg-rose-500/15 text-rose-400" },
  store_status: { icon: Store, cls: "bg-amber-500/15 text-amber-400" },
  driver_new: { icon: Truck, cls: "bg-sky-500/15 text-sky-400" },
  chat_message: { icon: MessageCircle, cls: "bg-pink-500/15 text-pink-400" },
  system: { icon: Megaphone, cls: "bg-zinc-500/15 text-zinc-400" },
};

const FILTERS = [
  { k: "all", label: "Tudo" },
  { k: "orders", label: "Pedidos" },
  { k: "stores", label: "Lojas" },
  { k: "drivers", label: "Entregadores" },
  { k: "messages", label: "Mensagens" },
];

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export const FranchiseeNotificationsBell = () => {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const panelRef = useRef(null);

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const auth = { Authorization: `Bearer ${token}` };

  const fetchCount = useCallback(async () => {
    try {
      const r = await fetch(`${API}/franchisee/notifications/unread-count`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      if (r.ok) setUnread((await r.json()).unread_count);
    } catch (e) { console.warn("notif count", e); }
  }, []);

  const fetchList = useCallback(async (cat) => {
    try {
      const r = await fetch(`${API}/franchisee/notifications?category=${cat}&limit=60`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      if (r.ok) {
        const d = await r.json();
        setItems(d.items);
        setUnread(d.unread_count);
      }
    } catch (e) { console.warn("notif list", e); }
  }, []);

  useEffect(() => {
    fetchCount();
    const iv = setInterval(fetchCount, 20000);
    return () => clearInterval(iv);
  }, [fetchCount]);

  useEffect(() => {
    if (open) { setItems(null); fetchList(filter); }
  }, [open, filter, fetchList]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markRead = async (n) => {
    if (n.read) return;
    setItems((list) => list.map((i) => (i.id === n.id ? { ...i, read: true } : i)));
    setUnread((u) => Math.max(0, u - 1));
    try {
      await fetch(`${API}/franchisee/notifications/${n.id}/read`, { method: "PUT", headers: auth });
    } catch (e) { console.warn("mark read", e); }
  };

  const markAll = async () => {
    try {
      const r = await fetch(`${API}/franchisee/notifications/read-all`, { method: "PUT", headers: auth });
      if (r.ok) {
        setItems((list) => (list || []).map((i) => ({ ...i, read: true })));
        setUnread(0);
        toast.success("Todas as notificações marcadas como lidas");
      }
    } catch (e) { console.warn("mark all", e); }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative w-9 h-9 rounded-full hover:bg-white/5 border border-white/10 flex items-center justify-center"
        data-testid="fr-notifications-bell"
        aria-label="Notificações"
      >
        <Bell className="w-4 h-4 text-white/70" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-gradient-to-r from-orange-500 to-rose-500 text-white text-[9px] font-extrabold flex items-center justify-center shadow-[0_0_10px_rgba(255,107,0,0.6)]"
            data-testid="fr-notifications-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-12 w-[92vw] max-w-sm bg-[#13161A] border border-white/10 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden z-50"
            data-testid="fr-notifications-panel"
          >
            <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
              <h3 className="text-sm font-extrabold text-white">Notificações</h3>
              <button
                onClick={markAll}
                className="text-[10px] font-bold text-orange-400 hover:text-orange-300 flex items-center gap-1"
                data-testid="fr-notifications-mark-all"
              >
                <CheckCheck className="w-3.5 h-3.5" /> Marcar todas como lidas
              </button>
            </div>

            <div className="px-3 py-2 flex gap-1.5 overflow-x-auto border-b border-white/5">
              {FILTERS.map((f) => (
                <button
                  key={f.k}
                  onClick={() => setFilter(f.k)}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold border whitespace-nowrap ${
                    filter === f.k
                      ? "bg-orange-500/20 border-orange-400/60 text-orange-300"
                      : "bg-zinc-950 border-white/10 text-zinc-400 hover:text-white"
                  }`}
                  data-testid={`fr-notif-filter-${f.k}`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="max-h-[55vh] overflow-y-auto" data-testid="fr-notifications-list">
              {items === null ? (
                <div className="flex items-center justify-center py-10 text-zinc-500">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Carregando…
                </div>
              ) : items.length === 0 ? (
                <div className="py-10 text-center">
                  <Bell className="w-7 h-7 text-zinc-700 mx-auto mb-2" />
                  <p className="text-xs text-zinc-500">Nenhuma notificação.</p>
                </div>
              ) : (
                items.map((n) => {
                  const meta = TYPE_META[n.type] || TYPE_META.system;
                  const Icon = meta.icon;
                  return (
                    <button
                      key={n.id}
                      onClick={() => markRead(n)}
                      className={`w-full text-left px-4 py-2.5 flex items-start gap-3 border-b border-white/[0.03] hover:bg-white/[0.03] transition-colors ${!n.read ? "bg-orange-500/[0.04]" : ""}`}
                      data-testid={`fr-notif-item-${n.id}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${meta.cls}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs truncate ${!n.read ? "font-extrabold text-white" : "font-bold text-zinc-400"}`}>{n.title}</p>
                        <p className="text-[11px] text-zinc-500 truncate">{n.body}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className="text-[9px] text-zinc-600 font-bold">{timeAgo(n.created_at)}</span>
                        {!n.read && <span className="w-2 h-2 rounded-full bg-orange-500 shadow-[0_0_6px_rgba(255,107,0,0.8)]" />}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
